import { Rect } from '../util';
import { ViewState } from './chart';
import { ChartMargin } from './chart-props';

export type TransformCameraOptions = {
    duration?: number;
};

export class Transform {
    // Base margin
    private margin: ChartMargin = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    };

    private transform: ViewState = {
        scaleX: 1,
        scaleY: 1,
        zoom: [1, 1],
        translateX: 0,
        translateY: 0,
        canvasWidth: 0,
        canvasHeight: 0,
        pixelRatio: 1,
        chartViewport: new Rect([0, 0], { width: 0, height: 0 }),
        chartArea: new Rect([0, 0], { width: 0, height: 0 }),
        leftScaleArea: new Rect([0, 0], { width: 0, height: 0 }),
        topScaleArea: new Rect([0, 0], { width: 0, height: 0 }),
        rightScaleArea: new Rect([0, 0], { width: 0, height: 0 }),
        bottomScaleArea: new Rect([0, 0], { width: 0, height: 0 })
    };
    private zoomTarget: {
        from: [number, number];
        to: [number, number];
        startTime: number;
        endTime: number;
        midpoint: [number, number];
    } | null = null;

    zoom(zoom: [number, number], midpoint: [number, number], options?: TransformCameraOptions) {
        if (options && options.duration) {
            this.zoomTarget = {
                startTime: performance.now(),
                endTime: performance.now() + options.duration,
                from: this.transform.zoom,
                to: zoom,
                midpoint
            };
        } else {
            // X
            {
                this.transform.zoom = zoom;
                const newScale = Math.pow(2, zoom[0] - 1);
                const x_m = midpoint[0] - this.transform.chartViewport.left;
                const w1 = this.transform.scaleX * this.transform.chartViewport.width;
                const deltaW = (newScale - this.transform.scaleX) * this.transform.chartViewport.width;
                const alpha = (-this.transform.translateX + x_m) / w1;
                const deltaM = alpha * deltaW;
                this.transform.scaleX = newScale;
                this.transform.translateX += -deltaM;
            }
            // Y
            {
                const newScale = Math.pow(2, zoom[1] - 1);
                const y_m = midpoint[1] - this.transform.chartViewport.top
                const h1 = this.transform.scaleY * this.transform.chartViewport.height;
                const deltaW = (newScale - this.transform.scaleY) * this.transform.chartViewport.height;
                const alpha = (-this.transform.translateY + y_m) / h1;
                const deltaM = alpha * deltaW;
                this.transform.scaleY = newScale;
                this.transform.translateY += -deltaM;
            }
        }
    }

    drag(deltaX: number, deltaY: number) {
        // TODO: add inertia
        this.transform.translateX += deltaX;
        this.transform.translateY += deltaY;
    }

    resetViewState() {
        this.transform.scaleX = 1;
        this.transform.scaleY = 1;
        this.transform.zoom = [1, 1];
        this.zoomTarget = null;
        this.transform.translateX = 0;
        this.transform.translateY = 0;
    }

    isMoving(): boolean {
        return !!this.zoomTarget;
    }

    resize(width: number, height: number, pixelRatio: number) {
        this.transform.canvasWidth = width;
        this.transform.canvasHeight = height;
        this.transform.pixelRatio = pixelRatio;
    }

    setMargin(margin: ChartMargin) {
        this.margin = margin;
    }

    getState() {
        return this.transform;
    }

    tick() {}

    update() {
        if (this.zoomTarget) {
            const now = performance.now();
            const value = linearEasing(this.zoomTarget.startTime, this.zoomTarget.endTime, now);
            this.zoom(
                [lerp(this.zoomTarget.from[0], this.zoomTarget.to[0], value), lerp(this.zoomTarget.from[1], this.zoomTarget.to[1], value)],
                this.zoomTarget.midpoint
            );
            if (value >= 1) {
                this.zoomTarget = null;
            }
        }

        const chartViewportWidth = this.transform.canvasWidth - this.margin.left - this.margin.right;
        const chartViewportHeight = this.transform.canvasHeight - this.margin.top - this.margin.bottom;

        // Make sure translate is within limits
        this.transform.translateX = Math.min(
            0,
            Math.max(this.transform.translateX, chartViewportWidth - chartViewportWidth * this.transform.scaleX)
        );
        this.transform.translateY = Math.min(
            0,
            Math.max(this.transform.translateY, chartViewportHeight - chartViewportHeight * this.transform.scaleY)
        );

        this.transform.chartViewport = new Rect([this.margin.left, this.margin.top], {
            width: chartViewportWidth,
            height: chartViewportHeight
        });
        this.transform.chartArea = new Rect([this.margin.left + this.transform.translateX, this.margin.top + this.transform.translateY], {
            width: this.transform.scaleX * chartViewportWidth,
            height: this.transform.scaleY * chartViewportHeight
        });
        this.transform.leftScaleArea = new Rect([0, this.margin.top], {
            width: this.margin.left,
            height: chartViewportHeight
        });
        this.transform.topScaleArea = new Rect([this.margin.left, 0], {
            width: chartViewportWidth,
            height: this.margin.top
        });
        this.transform.rightScaleArea = new Rect([this.transform.canvasWidth - this.margin.right, this.margin.top], {
            width: this.margin.right,
            height: chartViewportHeight
        });
        this.transform.bottomScaleArea = new Rect([this.margin.left, this.transform.canvasHeight - this.margin.bottom], {
            width: this.transform.canvasWidth - this.margin.left - this.margin.right,
            height: this.margin.bottom
        });
    }
}

function linearEasing(start: number, end: number, value: number): number {
    return Math.max(0, Math.min(1, (value - start) / (end - start)));
}

function lerp(x: number, y: number, a: number): number {
    return x * (1 - a) + y * a;
}
